import React, { useEffect, useState } from 'react';
import { Table, TextInput, rem, createStyles, Text, Center, Pagination, Flex, ScrollArea, Button, Skeleton, Title, Box, Modal, useMantineTheme, Portal, Overlay, Loader } from '@mantine/core';
import { IconCheck, IconSearch, IconUpload } from '@tabler/icons-react';
import { keys } from '@mantine/utils';
import { deleteTenantUser, getUsers } from '../../services/api.service';
import { notifications } from '@mantine/notifications';
import { useDisclosure } from '@mantine/hooks';
import DeleteButton from '../buttons/DeleteButton';
import { modals } from '@mantine/modals';
import TenantUserForm from '../forms/TenantUserForm';
import UploadUsersForm from '../forms/UploadUsersForm';
import { useSelector } from 'react-redux';

// Custom styles
const useStyles = createStyles((theme) => ({
	container: {
		padding: theme.spacing.md,
		height: '100%',
	},
	th: {
		padding: '0 !important',
	},
	control: {
		width: '100%',
		padding: `${theme.spacing.xs} ${theme.spacing.md}`,
		'&:hover': {
			backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[1],
		},
	},
	icon: {
		width: rem(21),
		height: rem(21),
		borderRadius: rem(21),
	},
}));

const TenantUsers = () => {

	// State and hooks
	const [isLoading, setIsLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [users, setUsers] = useState([]);
	const [isEdit, setIsEdit] = useState(false);
	const [userEdit, setUserEdit] = useState({});
	const [filteredData, setFilteredData] = useState([]);
	const { classes } = useStyles();
	const [total, setTotal] = useState(0);
	const [limit] = useState(10);
	const [page, setPage] = useState(1);
	const offset = page - 1;
	const [upOpened, upHandlers] = useDisclosure(false);
	const [nuOpened, nuHandlers] = useDisclosure(false);
	const [isDeleteLoading, toggleDeleteLoading] = useState(false);
	const theme = useMantineTheme();
	const tenant = useSelector(state => state.authData.tenant);

	// On load
	useEffect(() => {
		getUsersList();
		// eslint-disable-next-line
	}, []);

	// Get beta users
	const getUsersList = () => {

		// Get
		getUsers(`?limit=${limit}&offset=${offset}`, function (response) {

			// Set beta users and initial sorted data, remove loader
			setUsers(response.data.data);
			setFilteredData(response.data.data);
			setTotal(response.data.total);
			setIsLoading(false);

		}, function (error) {

			// Remove loader
			setIsLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	// TH component
	const Th = ({ children, width }) => {
		const { classes } = useStyles();
		return (
			<th className={classes.th} width={width}>
				<Text
					fw={700}
					fz="sm"
					className={classes.control}
				>
					{children}
				</Text>
			</th>
		);
	}

	// Build table head
	const ths = (
		<tr>
			<Th>
				ID
			</Th>
			<Th>
				Correo Electrónico
			</Th>
			{!tenant && (<Th></Th>)}
		</tr>
	);

	// Build rows
	const rows = filteredData.map((element) => (
		<tr
			style={{ cursor: 'pointer' }}
			key={element.id}
			onClick={(event) => toggleUserEdit(event, element)}
		>
			<td width={100}>
				{element.id}
			</td>
			<td>
				{element.email}
			</td>
			{!tenant && (<td>
				<DeleteButton
					onClick={(e) => {
						e.stopPropagation();
						openDeleteModal(element.id);
					}}
				/>
			</td>)}
		</tr>
	));

	// Handle search change
	const handleSearchChange = (event) => {
		const { value } = event.currentTarget;
		setSearch(value);
		filterData(users, value);
	};

	// Filter and return from query/search/sort
	const filterData = (data, search) => {
		const query = search.toLowerCase().trim();
		setFilteredData(data.filter((item) => keys(data[0]).some((key) => item[key]?.toString().toLowerCase().includes(query))));
	}

	// Listen to page changes
	useEffect(() => {

		// Get information (if not initial)
		if (!isLoading) getUsersList();

		// eslint-disable-next-line
	}, [page]);

	// Open delete confirmation modal
	const openDeleteModal = (id) =>
		modals.openConfirmModal({
			title: 'Eliminar Noticia',
			centered: true,
			children: (
				<Text size="sm">
					¿Estás seguro de querer borrar este usuario? Esta acción no se puede deshacer.
				</Text>
			),
			labels: { confirm: 'Eliminar usuario', cancel: 'Cancelar' },
			confirmProps: { color: 'red', variant: 'light' },
			onCancel: () => { }, // Nothing
			onConfirm: () => triggerDeleteUser(id),
			closeOnClickOutside: false,
			zIndex: 400
		});

	// Trigger delete user
	const triggerDeleteUser = (id) => {

		// Set loader
		toggleDeleteLoading(true);

		// Call API
		deleteTenantUser(id, function () {

			// Show success
			notifications.show({
				title: 'Usuario eliminado correctamente',
				color: 'green',
				icon: <IconCheck />
			});

			// Close after 1 second
			setTimeout(() => {

				// Remove preloader
				toggleDeleteLoading(false);

				// Close modal
				closeModal();

			}, 1000);

		}, function (error) {

			// Remove preloader
			toggleDeleteLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	// Close posts modal
	const closeModal = () => {
		// Get users and close modal
		getUsersList();
		setIsEdit(false);
		setUserEdit({});
		nuHandlers.close();
	}

	// Close upload modal
	const closeUploadModal = () => {
		// Get users and close modal
		getUsersList();
		upHandlers.close();
	}

	// Toggle user edit
	const toggleUserEdit = (event, user) => {
		// Stop click and toggle edit
		event.stopPropagation();
		setIsEdit(true);
		setUserEdit(user);
		// Open modal
		nuHandlers.open();
	}

	return (
		<>
			{/* New User Modal */}
			<Modal
				opened={nuOpened}
				withCloseButton={false}
				centered
				size="xl"
				overlayProps={{
					color: theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.colors.gray[2],
					opacity: 0.55,
					blur: 3,
				}}
				closeOnClickOutside={false}
				closeOnEscape={false}
			>
				{/* New User Form */}
				<TenantUserForm
					closeModal={closeModal}
					isEdit={isEdit}
					user={userEdit}
				/>
			</Modal>
			{/* CSV Upload Modal */}
			<Modal
				opened={upOpened}
				withCloseButton={false}
				centered
				size="xl"
				overlayProps={{
					color: theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.colors.gray[2],
					opacity: 0.55,
					blur: 3,
				}}
				closeOnClickOutside={false}
				closeOnEscape={false}
			>
				{/* Upload Form */}
				<UploadUsersForm closeModal={closeUploadModal} />
			</Modal>
			{/* Delete Loader */}
			{isDeleteLoading ?
				<Portal>
					<Overlay
						sx={{ zIndex: 999 }}
						center
						blur={15}
					>
						<Loader size={100} />
					</Overlay>
				</Portal>
			: null}
			{/* Content */}
			<Box className="mainContainer">
				{/* Content */}
				<Flex
					direction="column"
					justify="space-between"
					className={classes.container}
				>
					{/* Title */}
					<Title mb="xl">
						Usuarios
					</Title>
					{/* Group */}
					<Flex direction="column">
						{/* Top */}
						<Flex mb="md">
							{/* New Load */}
							<Button
								onClick={upHandlers.open}
								variant="filled"
								leftIcon={<IconUpload size={18} />}
							>
								Cargar CSV
							</Button>
							{/* New User */}
							<Button
								ml="sm"
								onClick={nuHandlers.open}
								variant="filled"
							>
								Nuevo Usuario
							</Button>
							{/* Search */}
							<TextInput
								placeholder="Buscar usuarios"
								icon={<IconSearch size="0.9rem" stroke={1.5} />}
								value={search}
								onChange={handleSearchChange}
								ml="sm"
								sx={{
									width: '100%',
									maxWidth: 350
								}}
								variant="filled"
							/>
						</Flex>
						{/* Table */}
						<ScrollArea>
							{isLoading ?
								<>
									<Skeleton height={40} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
								</>
								:
								<Table
									highlightOnHover
									sx={{ flex: 2 }}
								>
									<thead>{ths}</thead>
									<tbody>{rows.length > 0 ? (
										rows
									) : (
										<tr>
											<td colSpan={!tenant ? 2 : 3}>
												<Text
													weight={500}
													align="center"
													my="xl"
													color="dimmed"
												>
													Sin resultados
												</Text>
											</td>
										</tr>
									)}</tbody>
								</Table>
							}
						</ScrollArea>
					</Flex>
					{/* Pages */}
					<Center sx={{ marginTop: 20 }}>
						<Pagination
							total={Math.ceil(total / limit)}
							siblings={1}
							defaultValue={1}
							withEdges
							onChange={setPage}
							value={page}
						/>
					</Center>
				</Flex>
			</Box>
		</>
	);

}

export default TenantUsers;
